import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Modal, Button } from '@core/components/ui';

/**
 * File/Folder Delete Confirmation Modal
 *
 * Modal dialog to confirm file or folder deletion with item name display.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Object|null} props.file - File or folder object to delete
 * @param {Function} props.onConfirm - Callback when deletion is confirmed
 * @param {Function} props.onCancel - Callback when deletion is cancelled
 *
 * @example
 * <FileDeleteModal
 *   isOpen={isOpen}
 *   file={selectedItem}
 *   onConfirm={handleDelete}
 *   onCancel={handleCancel}
 * />
 */
export function FileDeleteModal({ isOpen, file, onConfirm, onCancel }) {
  const { t } = useTranslation();

  const handleConfirm = useCallback(() => {
    if (file) {
      onConfirm(file.id);
    }
  }, [file, onConfirm]);

  const isFolder = file?.type === 'folder';
  const itemName = isFolder ? file?.name : file?.original_filename;
  const titleKey = isFolder ? 'files.folders.deleteConfirm' : 'file.deleteConfirm';
  const titleDefault = isFolder
    ? '¿Estás seguro de que quieres eliminar esta carpeta?'
    : '¿Estás seguro de que quieres eliminar este archivo?';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={t(titleKey, titleDefault)}
      size="sm"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onCancel}
            className="cursor-pointer"
          >
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            className="cursor-pointer"
          >
            {t('common.delete', 'Eliminar')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-subtle break-words">
        {itemName}
      </p>
    </Modal>
  );
}

FileDeleteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  file: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['file', 'folder']),
    original_filename: PropTypes.string,
    name: PropTypes.string,
  }),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
